import { useMemo } from 'react';
import endpoints from '@services/api/endpoints';
import useFetch from '@services/api/useFetch';
import { replaceUrlParams } from '@utils/string-util';

import type {
  VenueLeadFeedbackRequest,
  VenueLeadItem,
  VenueLeadPersonalized,
  VenueLeadSearchParams,
  VenueLeadSearchResults,
  VenueLeadTicket,
} from '../../../types/events/venue-leads';

export const useVenueLeadsApi = () => {
  const api = useFetch();
  return useMemo(() => {
    return {
      async getPersonalized(id: string): Promise<VenueLeadPersonalized> {
        return api.get(
          replaceUrlParams(endpoints.venueLeads.getPersonalized, {
            id,
          })
        );
      },
      async search(
        organizationId: string,
        params: Partial<VenueLeadSearchParams>
      ): Promise<VenueLeadSearchResults> {
        return api.get(endpoints.venueLeads.search, {
          organizationId,
          ...params,
        });
      },
      async isRunningTicket(organizationId: string): Promise<VenueLeadTicket> {
        return api.get(
          replaceUrlParams(endpoints.venueLeads.isRunning, { organizationId }),
          {
            organizationId,
          }
        );
      },
      async togglePin(id: string, pinned: boolean): Promise<VenueLeadItem> {
        return api.patch(
          replaceUrlParams(endpoints.venueLeads.togglePin, {
            id,
            pinned,
          })
        );
      },
      async addFeedback(
        id: string,
        params: VenueLeadFeedbackRequest
      ): Promise<VenueLeadPersonalized> {
        return api.patch(
          replaceUrlParams(endpoints.venueLeads.updateFeedback, {
            id,
          }),
          params
        );
      },
      async updateNotes(
        id: string,
        notes: string
      ): Promise<VenueLeadPersonalized> {
        return api.patch(
          replaceUrlParams(endpoints.venueLeads.updateNotes, {
            id,
          }),
          {
            notes,
          }
        );
      },
    };
  }, [api]);
};

export const venueLeadsApiKeys = {
  all: ['venue-leads'] as const,
  getPersonalized: (id: string) =>
    [...venueLeadsApiKeys.all, 'get-personalized', id] as const,
  isRunningTicket: (orgId: string) =>
    [...venueLeadsApiKeys.all, 'is-running-ticket', orgId] as const,
  search: (orgId: string, params?: Partial<VenueLeadSearchParams>) =>
    [...venueLeadsApiKeys.all, 'venue-leads-search', orgId, params].filter(
      Boolean
    ),
};
